import React, { useState, useEffect } from 'react';
import { X, RefreshCw, AlertTriangle, AlertOctagon, Info, Bell, Check } from 'lucide-react';

const MESSAGE_CONTEXTS = {
  update: { label: 'System Update', icon: RefreshCw, color: 'text-blue-400', border: 'border-blue-500/20', bg: 'bg-blue-500' },
  warning: { label: 'Warning', icon: AlertTriangle, color: 'text-amber-400', border: 'border-amber-500/20', bg: 'bg-amber-500' },
  alert: { label: 'Critical Alert', icon: AlertOctagon, color: 'text-rose-400', border: 'border-rose-500/20', bg: 'bg-rose-500' },
  general: { label: 'General', icon: Info, color: 'text-slate-400', border: 'border-slate-500/20', bg: 'bg-slate-500' },
  notification: { label: 'Notification', icon: Bell, color: 'text-emerald-400', border: 'border-emerald-500/20', bg: 'bg-emerald-500' },
};

export default function SystemMessageModal({ message, onAcknowledge }) {
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  
  if (!message) return null;

  const theme = MESSAGE_CONTEXTS[message.context] || MESSAGE_CONTEXTS.general;
  const isMandatory = message.mandatory?.required;
  const needsPrivacy = message.mandatory?.agreements?.privacy;
  const needsTerms = message.mandatory?.agreements?.terms;

  // Logic to prevent continuing if mandatory checkboxes aren't ticked
  const canAcknowledge = !isMandatory || ((!needsPrivacy || agreedPrivacy) && (!needsTerms || agreedTerms));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="w-full max-w-lg relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl bg-slate-900/40 backdrop-blur-xl animate-in zoom-in-95 duration-300 ring-1 ring-white/5">
        
        {/* Header */}
        <div className="px-6 py-5 flex items-start justify-between border-b border-white/5">
          <div>
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2 border ${theme.border} bg-opacity-20 ${theme.bg} ${theme.color}`}>
              {theme.label}
            </div>
            <h3 className="text-xl font-semibold text-white tracking-tight leading-snug">
              {message.header}
            </h3>
          </div>
          {!isMandatory && (
            <button onClick={() => onAcknowledge(message)} className="text-slate-500 hover:text-white transition-colors p-1 hover:bg-white/5 rounded-lg">
              <X size={18} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="text-sm text-slate-300 leading-relaxed font-light whitespace-pre-wrap">
            {message.body}
          </div>

          {/* Mandatory Checkboxes */}
          {isMandatory && (needsPrivacy || needsTerms) && (
            <div className="mt-6 space-y-3 pt-6 border-t border-white/5">
              {needsPrivacy && (
                <label className="flex items-start gap-3 group cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={agreedPrivacy} 
                    onChange={(e) => setAgreedPrivacy(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-xs text-slate-400 font-light">
                    I agree to the <a href={message.mandatory.agreements.privacyUrl} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 underline">Privacy Policy</a>.
                  </span>
                </label>
              )}
              {needsTerms && (
                <label className="flex items-start gap-3 group cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={agreedTerms} 
                    onChange={(e) => setAgreedTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-xs text-slate-400 font-light">
                    I accept the <a href={message.mandatory.agreements.termsUrl} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 underline">Terms & Conditions</a>.
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white/5 flex gap-3">
          <button 
            disabled={!canAcknowledge}
            onClick={() => onAcknowledge(message)}
            className={`flex-1 font-semibold py-2.5 rounded-lg transition-all text-sm flex items-center justify-center gap-2
              ${canAcknowledge 
                ? 'bg-white text-slate-900 hover:bg-slate-200 shadow-lg shadow-white/5' 
                : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'}`}
          >
            {isMandatory ? <Check size={16} /> : null}
            {isMandatory ? 'Accept and Continue' : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );
}